import * as E from 'fp-ts/lib/Either';
import {reporter} from 'io-ts-reporters';
import {isString} from 'lodash';
import * as z3 from 'zod/v3';
import * as z4 from 'zod/v4';

import {AnySchemaType, ApiType, SchemaValidationResult, TypeOfSchema} from '../../models/api/commonApiTypes';
import {inIdleOrHiddenCallbackAsync} from '../async/inIdleCallback';
import {
  annotateTaskTiming,
  TaskSourcesEnum,
  TaskTimingTypesEnum,
} from '../instrumentation/eventLoopPerformanceHelpers';
import {ApiBadResponseError, IOTSError} from './apiErrorHelpers';
import {isZodSchema} from './zodHelpers';

/*
 * Model parsing.
 */

/** Validate that a plain JS object conforms to the schema described in the specified {@link IApiType} */
export function readApiModel<T extends AnySchemaType>(apiType: ApiType<T>, src: any): TypeOfSchema<T> {
  ensureParseableApiResponse(src);

  if (apiType.dangerouslyTransformRaw) {
    const instrumentionStartTime = performance.now();
    try {
      // Try to transform the raw data and then parse/validate it.
      const transformedSrc = apiType.dangerouslyTransformRaw(src);
      const instrumentationDuration = performance.now() - instrumentionStartTime;
      const {model, parseStartTime, parseDuration, validationStartTime, validationDuration} =
        parseAndValidateModel(apiType, transformedSrc);

      // If the validation succeeds, count the transform time as the instrumentation duration and annotate
      // all the durations.
      annotateTaskTiming(
        TaskTimingTypesEnum.INSTRUMENTATION,
        instrumentionStartTime,
        instrumentationDuration,
        'experimentalTransform',
      );
      annotateTaskTiming(TaskTimingTypesEnum.IOTS_PARSE, parseStartTime, parseDuration, apiType.name);
      annotateTaskTiming(
        TaskTimingTypesEnum.IOTS_VALIDATION,
        validationStartTime,
        validationDuration,
        apiType.name,
      );

      return model;
    } catch (error) {
      // If the validation fails, count the elapsed time so far as the instrumentation duration.
      annotateTaskTiming(
        TaskTimingTypesEnum.INSTRUMENTATION,
        instrumentionStartTime,
        performance.now() - instrumentionStartTime,
        'experimentalTransform',
      );

      apiType.reportError('iots validation failure after experimental transform', {
        model_name: apiType.name,
        error_message: error?.message,
      });
    }
  }

  // Parse/validate the original source.
  const {model, parseStartTime, parseDuration, validationStartTime, validationDuration} =
    parseAndValidateModel(apiType, src);

  annotateTaskTiming(TaskTimingTypesEnum.IOTS_PARSE, parseStartTime, parseDuration, apiType.name);
  annotateTaskTiming(
    TaskTimingTypesEnum.IOTS_VALIDATION,
    validationStartTime,
    validationDuration,
    apiType.name,
  );

  return model;
}

export async function readApiModelInIdleCallbackAsync<T extends AnySchemaType>(
  apiType: ApiType<T>,
  src: any,
) {
  ensureParseableApiResponse(src);

  // Parse the object
  const modelParsed = await inIdleOrHiddenCallbackAsync(() => apiType.parse(src), {
    source: TaskSourcesEnum.IOTS,
  });

  // Validate it.
  const modelValidated = await inIdleOrHiddenCallbackAsync(() => decodeApiModel(apiType, modelParsed), {
    source: TaskSourcesEnum.IOTS,
  });

  return ensureValidApiModel(apiType, src, modelValidated);
}

function ensureParseableApiResponse(src: any) {
  const isHtmlResponse = isString(src) && src.includes('<html');
  if (isHtmlResponse) {
    throw new ApiBadResponseError(src);
  }
}

function ensureValidApiModel<T extends AnySchemaType>(
  apiType: ApiType<T>,
  src: any,
  modelValidated: SchemaValidationResult<T>,
) {
  if (modelValidated.success) {
    return modelValidated.data;
  }

  const errors = modelValidated.error;
  if (errors instanceof z3.ZodError) {
    const flattenedErrors = errors.flatten();
    throw new IOTSError(
      apiType.name,
      [
        ...(flattenedErrors.formErrors ?? []),
        ...Object.entries(flattenedErrors.fieldErrors).flatMap(([key, value]) => `${key}: ${value}`),
      ].join(', '),
      JSON.stringify(src, null, 2).slice(0, 10000),
    );
  }

  if (errors instanceof z4.ZodError) {
    throw new IOTSError(apiType.name, z4.prettifyError(errors), JSON.stringify(src, null, 2).slice(0, 10000));
  }

  // io-ts errors.
  reporter({_tag: 'Left', left: errors}).forEach((err) => {
    throw new IOTSError(apiType.name, err, JSON.stringify(src, null, 2).slice(0, 10000));
  });

  throw new Error('[io-ts] This error should not be reached.');
}

export function decodeApiModel<T extends AnySchemaType>(
  apiType: ApiType<T>,
  src: any,
): SchemaValidationResult<T> {
  if (isZodSchema(apiType.runtimeType)) {
    return apiType.runtimeType.safeParse(src);
  }

  const result = apiType.runtimeType.decode(src);

  if (E.isRight(result)) {
    return {success: true, data: result.right};
  }

  return {success: false, error: result.left};
}

interface ParseAndValidateResult<T extends AnySchemaType> {
  model: TypeOfSchema<T>;
  parseDuration: number;
  validationDuration: number;
  parseStartTime: number;
  validationStartTime: number;
}
function parseAndValidateModel<T extends AnySchemaType>(
  apiType: ApiType<T>,
  src: any,
): ParseAndValidateResult<T> {
  // Parse the object.
  const parseStartTime = performance.now();
  const modelParsed = apiType.parse(src);
  const parseDuration = performance.now() - parseStartTime;

  // Validate it.
  const validationStartTime = performance.now();

  const validationResult = decodeApiModel(apiType, modelParsed);
  const modelValidated = ensureValidApiModel(apiType, src, validationResult);
  const validationDuration = performance.now() - validationStartTime;

  return {model: modelValidated, parseDuration, validationDuration, parseStartTime, validationStartTime};
}
